/**
 * Build-time runtime configuration resolved from Vite env vars.
 *
 * Everything here is pure so the resolution logic can be unit-tested without a
 * browser. `import.meta.env` values are statically replaced by Vite at build
 * time; this module is the single place that turns those strings into the
 * typed configuration the API client consumes.
 */

/** The §4 `servers[0].url` default for local development (Wave-3 backend lane). */
export const DEFAULT_API_ORIGIN = 'http://localhost:8000/v1';

/** Placeholder workspace for DEV-auth mode until a real membership is selected. */
export const DEFAULT_WORKSPACE_ID = '00000000-0000-4000-8000-000000000001';

/** The subset of Vite env vars this SPA reads. */
export interface BuildEnv {
  VITE_API_ORIGIN?: string;
  VITE_WORKSPACE_ID?: string;
  VITE_USE_MOCK?: string;
}

export interface RuntimeConfig {
  /** Absolute or path origin the generated client prefixes requests with. */
  apiOrigin: string;
  /** `X-Workspace-ID` header value (§4.4 — a routing hint, never proof of access). */
  workspaceId: string;
  /** Whether the §4 mock fetch interceptor should serve requests. */
  useMock: boolean;
}

/**
 * Resolve the API origin from `VITE_API_ORIGIN`, applying the §4 default when
 * unset/blank, and normalizing away a trailing slash so openapi-fetch never
 * emits a `//` between origin and path.
 */
export function resolveApiOrigin(raw: string | undefined): string {
  const value = raw === undefined ? '' : raw.trim();
  if (value === '') return DEFAULT_API_ORIGIN;
  return value.replace(/\/+$/, '');
}

/**
 * The mock is opt-in. Only the literal strings `"1"` or `"true"` enable it; the
 * default build talks to the real backend.
 */
export function resolveUseMock(raw: string | undefined): boolean {
  return raw === '1' || raw === 'true';
}

export function resolveWorkspaceId(raw: string | undefined): string {
  const value = raw === undefined ? '' : raw.trim();
  return value === '' ? DEFAULT_WORKSPACE_ID : value;
}

export function resolveRuntimeConfig(env: BuildEnv): RuntimeConfig {
  return {
    apiOrigin: resolveApiOrigin(env.VITE_API_ORIGIN),
    workspaceId: resolveWorkspaceId(env.VITE_WORKSPACE_ID),
    useMock: resolveUseMock(env.VITE_USE_MOCK),
  };
}
